/**
 * workspace-store.ts — Zustand store cho workspace switcher state.
 *
 * CR-WS-006 | TASK-UI-WS006-001, TASK-WS006-001
 *
 * Chỉ lưu: currentWorkspaceId + recentWorkspaceIds (tối đa 5).
 * Danh sách đầy đủ workspaces[] sống trong React Query (useMyWorkspaces).
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export interface WorkspaceState {
  currentWorkspaceId: string | null;
  activeWorkspaceId: string | null; // Alias for currentWorkspaceId
  recentWorkspaceIds: string[]; // max 5, FIFO

  setCurrentWorkspace: (id: string) => void;
  setActiveWorkspace: (id: string) => void; // Alias for setCurrentWorkspace
  addRecentWorkspace: (id: string) => void;
  addRecent: (id: string) => void; // Alias for addRecentWorkspace
  clearCurrentWorkspace: () => void;
}

const MAX_RECENT = 5;

/** Thêm id vào đầu list, dedup, giới hạn MAX_RECENT. */
function addToRecent(existing: string[], id: string): string[] {
  const deduped = [id, ...existing.filter((x) => x !== id)];
  return deduped.slice(0, MAX_RECENT);
}

const memoryMap = new Map<string, string>();

const safeStorage = createJSONStorage(() => {
  if (typeof window !== "undefined" && window.localStorage && typeof window.localStorage.getItem === "function") {
    return window.localStorage;
  }
  return {
    getItem: (key: string) => memoryMap.get(key) ?? null,
    setItem: (key: string, value: string) => memoryMap.set(key, value),
    removeItem: (key: string) => memoryMap.delete(key),
  };
});

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set) => ({
      currentWorkspaceId: null,
      get activeWorkspaceId() {
        return this.currentWorkspaceId;
      },
      recentWorkspaceIds: [],

      setCurrentWorkspace: (id: string) =>
        set((state) => {
          const next = addToRecent(state.recentWorkspaceIds, id);
          return {
            currentWorkspaceId: id,
            activeWorkspaceId: id,
            recentWorkspaceIds: next,
          };
        }),

      setActiveWorkspace: (id: string) =>
        set((state) => {
          const next = addToRecent(state.recentWorkspaceIds, id);
          return {
            currentWorkspaceId: id,
            activeWorkspaceId: id,
            recentWorkspaceIds: next,
          };
        }),

      addRecentWorkspace: (id: string) =>
        set((state) => ({
          recentWorkspaceIds: addToRecent(state.recentWorkspaceIds, id),
        })),

      addRecent: (id: string) =>
        set((state) => ({
          recentWorkspaceIds: addToRecent(state.recentWorkspaceIds, id),
        })),

      clearCurrentWorkspace: () =>
        set({ currentWorkspaceId: null, activeWorkspaceId: null }),
    }),
    {
      name: "workspace-store", // localStorage key
      storage: safeStorage,
      partialize: (state) => ({
        currentWorkspaceId: state.currentWorkspaceId,
        recentWorkspaceIds: state.recentWorkspaceIds,
      }),
    }
  )
);
